import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { ACCIDENT_REPORTS_LIST, ACCIDENT_REPORT_SIGNATURE } from '../../api/constants';

// Fetch accident reports list
export const fetchAccidentReports = createAsyncThunk(
  'accidentReport/fetchAccidentReports',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      
      const response = await apiHandler(ACCIDENT_REPORTS_LIST, 'POST', formData);
      return response.data.data.data; // response.data.data.data contains { meta, data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accident reports');
    }
  }
);

// Submit parent signature
export const submitParentSignature = createAsyncThunk(
  'accidentReport/submitParentSignature',
  async ({ reportId, parent_signature, parent_signature_date }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('id', reportId);
      formData.append('parent_signature', parent_signature);
      formData.append('parent_signature_date', parent_signature_date);
      
      const response = await apiHandler(ACCIDENT_REPORT_SIGNATURE, 'POST', formData);
      return { reportId, data: response.data.data.FormAdministrationMedication };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit signature');
    }
  }
);

const accidentReportSlice = createSlice({
  name: 'accidentReport',
  initialState: {
    reports: [],
    meta: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch accident reports
      .addCase(fetchAccidentReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccidentReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAccidentReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit parent signature
      .addCase(submitParentSignature.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitParentSignature.fulfilled, (state, action) => {
        state.submitLoading = false;
        // Update the report in the list with the new signature
        const reportIndex = state.reports.findIndex(r => r.id === action.payload.reportId);
        if (reportIndex !== -1) {
          state.reports[reportIndex].parent_signature = action.payload.data.parent_signature;
          state.reports[reportIndex].parent_signature_date = action.payload.data.parent_signature_date;
        }
      })
      .addCase(submitParentSignature.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      });
  }
});

export const { clearErrors } = accidentReportSlice.actions;

// Selectors
export const selectReports = (state) => state.accidentReport.reports;
export const selectMeta = (state) => state.accidentReport.meta;
export const selectLoading = (state) => state.accidentReport.loading;
export const selectError = (state) => state.accidentReport.error;
export const selectSubmitLoading = (state) => state.accidentReport.submitLoading;
export const selectSubmitError = (state) => state.accidentReport.submitError;

export default accidentReportSlice.reducer;
